<script setup lang="ts">
  import { storeToRefs } from "pinia";
  import { StatusBadge } from "@/components/episto-ui/status-badge";
  import { Switch } from "@/components/ui/switch";
  import { Tooltip } from "@/components/ui/tooltip";
  import { useCCampaignsStore } from "@/stores/cCampaigns";
  import { M_CAMPAIGN_STATUS_VARIANTS } from "@/components/MCampaign/mCampaignStatus";
  import type { CCampaignSummary } from "@/types";

  const props = defineProps<{
    mCampaignToken: string;
    mCampaignName: string;
  }>();

  const cCampaignsStore = useCCampaignsStore();
  const { cCampaigns } = storeToRefs(cCampaignsStore);
  const { toggleCCampaignImmediate } = cCampaignsStore;

  const isDisabled = (cCampaign: CCampaignSummary) => cCampaign.external_id === null;

  const label = (cCampaign: CCampaignSummary) =>
    cCampaign.name?.replace(`${props.mCampaignName} -`, "").trim() || cCampaign.name || "";

  const BADGE_STATUSES = ["paused", "pending", "error"];
  const shownStatus = (cCampaign: CCampaignSummary) => (cCampaign.effective_status || cCampaign.status || "").toLowerCase();
  const needsBadge = (cCampaign: CCampaignSummary) =>
    !isDisabled(cCampaign) && BADGE_STATUSES.includes(shownStatus(cCampaign));
</script>

<template>
  <div class="divide-y divide-secondary">
    <div
      v-for="cCampaign in cCampaigns as CCampaignSummary[]"
      :key="cCampaign.group_token"
      class="flex items-center gap-3 py-2.5 pr-8 pl-17">
      <Tooltip
        :content="isDisabled(cCampaign) ? 'Push the campaign first' : ''"
        :disabled="!isDisabled(cCampaign)">
        <span class="inline-flex shrink-0">
          <Switch
            :model-value="cCampaign.status === 'active'"
            :disabled="isDisabled(cCampaign)"
            @update:model-value="toggleCCampaignImmediate(props.mCampaignToken, cCampaign.group_token)" />
        </span>
      </Tooltip>
      <span
        class="min-w-0 truncate text-13 text-foreground"
        :title="cCampaign.name">
        {{ label(cCampaign) }}
      </span>
      <StatusBadge
        v-if="needsBadge(cCampaign)"
        :status="shownStatus(cCampaign)"
        :variants="M_CAMPAIGN_STATUS_VARIANTS"
        class="shrink-0" />
    </div>
  </div>
</template>
